import type { Customer } from '../../pages/cardex/api/useCustomer';

import { canalDe } from '../config/canaux';
import { heureCourte, initiales, nomComplet } from '../helpers/conversation';
import { t } from '../i18n/langue';

import Text from './Text';

/**
 * Une ligne de la liste des conversations — #16, variante A.
 *
 * **Avatar + pastille de canal, nom, extrait sur UNE ligne, heure, point de non
 * lu.** Cette unique ligne d'extrait est l'acquis le plus cher de #16 : c'est
 * elle qui disqualifie toute pastille de dossier (un pixel mort dans 99,9 % des
 * cas, pour 21 conversations dans tout le parc), et les lignes denses à deux
 * niveaux de la variante B.
 *
 * **Le nom de l'hôtel n'apparaît qu'en scope groupe** (#25). Sur un hôtel unique
 * il est redondant avec l'en-tête, et il coûterait la largeur qu'on vient de
 * défendre. Sous sélection de groupe il est en revanche **obligatoire** : sans
 * lui, les conversations de cinq hôtels sont indiscernables, et l'erreur chère
 * est de répondre au client du mauvais hôtel.
 *
 * Un `<button>` et non un `<div onClick>` : un élément tapotable est un contrôle
 * (#34), et le socle en fait une zone de 44 px par construction.
 */
interface LigneConversationProps {
    conversation: Customer;
    /** Vrai quand l'établissement sélectionné est un groupe : la ligne nomme alors son hôtel. */
    montrerHotel: boolean;
    onPress: () => void;
}

export default function LigneConversation({
    conversation,
    montrerHotel,
    onPress,
}: LigneConversationProps) {
    const canal = canalDe(conversation.last_message_action_type_id);
    const nonLu = (conversation.unread_count ?? 0) > 0;
    const hotel = conversation.last_message_facility_name;

    return (
        // mobile-check-ignore — la ligne EST le contrôle : un <div onClick> serait
        // le défaut que #34 interdit, et le socle n'a pas de primitive de ligne.
        <button
            type="button"
            onClick={onPress}
            className="flex w-full items-center gap-3 px-4 py-3 text-left active:bg-main-color-light"
        >
            <span className="relative shrink-0">
                <span className="flex h-11 w-11 items-center justify-center rounded-full bg-main-color-light text-base font-semibold text-main-color-dark">
                    {initiales(conversation)}
                </span>
                <span
                    className={`absolute -bottom-0.5 -right-0.5 flex h-5 w-5 items-center justify-center rounded-full border-2 border-white text-white ${canal.fond}`}
                >
                    <canal.Icone className="h-3 w-3" aria-hidden="true" />
                    <span className="sr-only">{canal.libelle}</span>
                </span>
            </span>

            <span className="min-w-0 flex-1">
                <span className="flex items-baseline justify-between gap-2">
                    <Text
                        as="span"
                        variante="corps"
                        className={`truncate ${nonLu ? 'font-semibold' : ''}`}
                    >
                        {nomComplet(conversation)}
                    </Text>
                    <Text
                        as="span"
                        variante="petit"
                        ton={nonLu ? 'marque' : 'attenue'}
                        className="shrink-0"
                    >
                        {heureCourte(conversation.last_message_at)}
                    </Text>
                </span>

                {/* Le nom de l'hôtel avant l'extrait : c'est le contexte qui décide
                    si l'on ouvre, pas le contenu. */}
                {montrerHotel && hotel && (
                    <Text as="span" variante="petit" ton="marque" className="block truncate">
                        {hotel}
                    </Text>
                )}

                <span className="flex items-center gap-2">
                    {/* Le bot IA a passé la main et personne n'a encore repris : c'est la
                        seule pastille qui mérite la largeur défendue plus haut — elle dit
                        qu'un visiteur attend un humain, maintenant. */}
                    {conversation.bot_escalated && (
                        <Text as="span" variante="petit" className="shrink-0 rounded-full bg-warning-color/20 px-1.5 py-0.5 font-medium leading-none">
                            {t('messagerie.ligne.botVersHumain')}
                        </Text>
                    )}
                    <Text
                        as="span"
                        variante="petit"
                        ton={nonLu ? 'normal' : 'attenue'}
                        className="min-w-0 flex-1 truncate"
                    >
                        {conversation.last_message_is_received === false && (
                            <span className="text-text-color-light">{t('messagerie.ligne.vous')}</span>
                        )}
                        {conversation.last_message_preview ?? '—'}
                    </Text>
                    {nonLu && (
                        <span
                            className="h-2 w-2 shrink-0 rounded-full bg-main-color"
                            aria-label={t('messagerie.ligne.nonLu')}
                            role="img"
                        />
                    )}
                </span>
            </span>
        </button>
    );
}
